import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { useTheme } from 'styled-components';
import * as S from './BusinessTariffs.styles';
import {
  getShopPackagesFromData,
  getPackageByName,
  getInitialTariffFromData,
  getDurationMultiplier,
  getCalculatedPrice,
  getCalculatedWalletAmount,
  getCalculatedPaidAdsCount,
  getTariffNames,
} from './BusinessTariffs.helpers';
import { useTariffCarousel } from './useTariffCarousel';
import { useDurationPill } from './useDurationPill';
import { GET_PACKAGES_LIST } from '@/api/queries/business.queries';
import type { Package, PackagesListData } from '@/api/types/business.types';

interface BusinessTariffsProps {
  packagesListData?: PackagesListData | null;
}

export const BusinessTariffs: React.FC<BusinessTariffsProps> = ({
  packagesListData: initialPackagesListData,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const mobileBreakpointPx = useMemo(
    () => parseInt(theme.breakpoints.sm, 10),
    [theme.breakpoints.sm]
  );
  const { data: clientData } = useQuery<PackagesListData>(GET_PACKAGES_LIST, {
    skip: !!initialPackagesListData,
  });

  const data = initialPackagesListData || clientData;
  const shopPackages = useMemo(() => getShopPackagesFromData(data), [data]);

  const [selectedTariff, setSelectedTariff] = useState<string>(() =>
    getInitialTariffFromData(data)
  );
  const [selectedDuration, setSelectedDuration] = useState('30');

  const tariffNames = useMemo(
    () => getTariffNames(shopPackages),
    [shopPackages]
  );
  const packagesByTariff = useMemo<Record<string, Package | null>>(
    () =>
      Object.fromEntries(
        tariffNames.map(tariff => [
          tariff,
          getPackageByName(shopPackages, tariff),
        ])
      ),
    [shopPackages, tariffNames]
  );
  const cardsViewportRef = useRef<HTMLDivElement>(null);
  const tariffsListRef = useRef<HTMLDivElement>(null);
  const { translatePx, trackWidthPx, cardWidthPx, onTouchStart, onTouchEnd } =
    useTariffCarousel(
      cardsViewportRef,
      tariffNames,
      selectedTariff,
      setSelectedTariff,
      mobileBreakpointPx
    );
  const { durationsContainerRef, pillPosition, measurePillForButton } =
    useDurationPill(selectedDuration);

  const handleSelectTariff = useCallback(
    (tariff: string) => {
      if (tariff === selectedTariff) return;
      const nextPackage = getPackageByName(shopPackages, tariff);
      if (
        nextPackage &&
        !nextPackage.periods.some(p => p.value?.toString() === selectedDuration)
      ) {
        setSelectedDuration(nextPackage.periods[0]?.value?.toString() ?? '30');
      }
      setSelectedTariff(tariff);
    },
    [selectedTariff, selectedDuration, shopPackages]
  );

  useEffect(() => {
    if (shopPackages?.length && !selectedTariff) {
      setSelectedTariff(getInitialTariffFromData(data));
    }
  }, [shopPackages, selectedTariff, data]);

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      window.innerWidth > mobileBreakpointPx ||
      !selectedTariff
    ) {
      return;
    }
    const list = tariffsListRef.current;
    if (!list) return;
    const item = list.querySelector<HTMLElement>(
      `[data-tariff="${selectedTariff}"]`
    );
    if (!item) return;

    const itemLeft = item.offsetLeft;
    const itemWidth = item.offsetWidth;
    const listWidth = list.clientWidth;
    const maxScroll = Math.max(0, list.scrollWidth - listWidth);
    const targetLeft = itemLeft - listWidth / 2 + itemWidth / 2;
    list.scrollTo({
      left: Math.max(0, Math.min(targetLeft, maxScroll)),
      behavior: 'smooth',
    });
  }, [mobileBreakpointPx, selectedTariff]);

  const selectedPackage = useMemo(
    () => packagesByTariff[selectedTariff] ?? null,
    [packagesByTariff, selectedTariff]
  );
  const durationMultiplier = useMemo(
    () => getDurationMultiplier(selectedDuration),
    [selectedDuration]
  );

  const handleDurationClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const button = (e.target as HTMLElement).closest('[data-days]');
      const days = button?.getAttribute('data-days');
      if (days) setSelectedDuration(days);
    },
    []
  );

  return (
    <S.TariffsSection>
      <S.TariffsTitle>{t('business.tariffs.title')}</S.TariffsTitle>
      <S.TariffsContainer>
        {shopPackages && shopPackages.length > 0 && (
          <>
            <S.TariffsList ref={tariffsListRef}>
              {tariffNames.map(tariff => (
                <S.TariffItem
                  key={tariff}
                  data-tariff={tariff}
                  $active={selectedTariff === tariff}
                  onClick={() => handleSelectTariff(tariff)}
                >
                  {packagesByTariff[tariff]?.name ||
                    t(`business.tariffs.labels.${tariff}`)}
                  {selectedTariff === tariff && <S.TariffArrow />}
                </S.TariffItem>
              ))}
            </S.TariffsList>
            {selectedPackage && (
              <S.TariffDetails>
                <S.TariffCardsScrollWrapper
                  ref={cardsViewportRef}
                  onTouchStart={onTouchStart}
                  onTouchEnd={onTouchEnd}
                >
                  <S.TariffCardsTrack
                    $trackWidthPx={trackWidthPx}
                    $translatePx={translatePx}
                    $cardWidthPx={cardWidthPx}
                  >
                    {tariffNames.map(tariff => (
                      <TariffCard
                        key={tariff}
                        tariff={tariff}
                        pkg={packagesByTariff[tariff] ?? null}
                        selectedDuration={selectedDuration}
                        durationMultiplier={durationMultiplier}
                        t={t}
                      />
                    ))}
                  </S.TariffCardsTrack>
                </S.TariffCardsScrollWrapper>
                <S.TariffDurationsSection>
                  <S.TariffDurations
                    ref={durationsContainerRef}
                    onClick={handleDurationClick}
                    role="group"
                  >
                    {pillPosition.width > 0 && (
                      <S.TariffDurationSlider
                        $left={pillPosition.left}
                        $width={pillPosition.width}
                      />
                    )}
                    {selectedPackage.periods.map(period => {
                      const days = period.value?.toString() || '';
                      return (
                        <S.TariffDurationButton
                          key={days}
                          type="button"
                          data-days={days}
                          ref={el => measurePillForButton(el, days)}
                          $active={selectedDuration === days}
                        >
                          {t('business.tariffs.days', { days })}
                        </S.TariffDurationButton>
                      );
                    })}
                  </S.TariffDurations>
                </S.TariffDurationsSection>
              </S.TariffDetails>
            )}
          </>
        )}
      </S.TariffsContainer>
    </S.TariffsSection>
  );
};

interface TariffCardProps {
  tariff: string;
  pkg: Package | null;
  selectedDuration: string;
  durationMultiplier: number;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function TariffCard({
  tariff,
  pkg,
  selectedDuration,
  durationMultiplier,
  t,
}: TariffCardProps) {
  if (!pkg) return null;
  const price = getCalculatedPrice(pkg, selectedDuration);
  const walletAmount = getCalculatedWalletAmount(pkg, durationMultiplier);
  const paidAdsCount = getCalculatedPaidAdsCount(pkg, durationMultiplier);

  return (
    <S.TariffCardSnap>
      <S.TariffInfo>
        <S.TariffHeader>
          <S.TariffName>
            {pkg.name || t(`business.tariffs.labels.${tariff}`)}
          </S.TariffName>
          <S.TariffPrice>{price}</S.TariffPrice>
        </S.TariffHeader>
        <S.TariffFeatures>
          <S.TariffFeature>
            <S.TariffFeatureIcon $type="wallet" />
            <div>
              <S.TariffFeatureTitle>
                {t('business.tariffs.balance.title')}
              </S.TariffFeatureTitle>
              <S.TariffFeatureDesc>
                {walletAmount ?? t(`business.tariffs.${tariff}.balance`)}
              </S.TariffFeatureDesc>
            </div>
          </S.TariffFeature>
          <S.TariffFeature>
            <S.TariffFeatureIcon $type="ads" />
            <div>
              <S.TariffFeatureTitle>
                {t('business.tariffs.ads.title')}
              </S.TariffFeatureTitle>
              <S.TariffFeatureDesc>
                {paidAdsCount !== null
                  ? paidAdsCount
                  : t(`business.tariffs.${tariff}.ads`)}
              </S.TariffFeatureDesc>
            </div>
          </S.TariffFeature>
          <S.TariffFeature>
            <S.TariffFeatureIcon $type="discount" />
            <div>
              <S.TariffFeatureTitle>
                {t('business.tariffs.discount.title')}
              </S.TariffFeatureTitle>
              <S.TariffFeatureDesc>
                {pkg.discount
                  ? `до ${pkg.discount}%`
                  : t(`business.tariffs.${tariff}.discount`)}
              </S.TariffFeatureDesc>
            </div>
          </S.TariffFeature>
        </S.TariffFeatures>
      </S.TariffInfo>
    </S.TariffCardSnap>
  );
}
